/**
 * File: assets/basari-product-drawer.js
 * Purpose: Lens Drawer open/close + multi-step named route navigation.
 * Sprint: 1B.8 — Drawer / 1B.9 — Options / 1B.10 — Hub / 1B.11 — Methods
 * Extended: 1B.12 Phase A — Manual Prescription foundation (lazy controller wire-up)
 * Extended: 1B.12 Phase B — Manual Prescription form UI mount (Controller-only)
 * Extended: 1B.13 Phase A — Upload Prescription architecture (state + services)
 * Extended: 1B.13 Phase B — Upload Prescription form UI mount (Controller-only)
 * Extended: 1B.14 Phase A — Lens Add-ons route + foundation (architecture only)
 * Extended: 1B.14 Phase B — Lens Add-ons Selection UI mount
 * Extended: Lens Add-ons Confirm → Dawn cart-drawer lifecycle (product-form parity)
 * Extended: 1B.16A — Eye Test Booking routes + foundation (architecture only)
 * Owner: Basari Theme / PDP
 * Dependencies: [data-basari-lens-drawer], [data-basari-open-lens-flow], basari-prescription.js, basari-prescription-form.js, basari-prescription-upload-form.js, basari-lens-addons.js, basari-lens-addons-form.js; Dawn cart-drawer + routes + pubsub
 * Note: basari-eye-test-booking.js is lazy-loaded via data-eye-test-script on first Eye Test route entry.
 *       basari-eye-test-store.js mounts Store Selection UI (1B.16B) after foundation init.
 *       basari-eye-test-date-time.js mounts Date & Time UI (1B.16C) after foundation init.
 * Future Sprint: VisionCarePro payload, eye-test date/time UI / maps / APIs
 *
 * Responsibilities:
 *   - Open / close drawer (overlay, ESC, focus trap, scroll lock)
 *   - Named step navigation via navigate() / renderStep()
 *   - Vision type → options layout routing
 *   - Vision Type video preview overlay (does not select or navigate)
 *   - Progressive / Bifocal subtype filtering
 *   - Package selection → Prescription Hub
 *   - Prescription choice → Methods or placeholder destinations
 *   - Prescription method → destinations (manual + upload UI + placeholders)
 *   - Lazy Prescription Framework init on manual / upload prescription enter
 *   - Lazy Manual / Upload Prescription form UI mount (does not mutate controller architecture)
 *   - Lens Add-ons named route + controller lifecycle (architecture only)
 *   - Eye Test Booking named routes + on-demand script + controller lifecycle (architecture only)
 *   - Router history for Lens Add-ons / Eye Test back navigation
 *   - Temporary Confirm → proceedToLensAddons() bridge
 *   - Lens Add-ons Confirm → AJAX add + Dawn cart.renderContents() (opens cart drawer)
 *   - Theme Editor reinitialization (shopify:section:load)
 *
 * Eye Test booking state resets (centralized via resetEyeTestBookingState):
 *   - Drawer closes / full vision reset
 *   - Product / variant identity changes
 *   - Prescription choice or method reset
 *   - Vision type change (via resetPrescriptionChoice in selectVisionType)
 *   - NOT during Back navigation inside the Eye Test flow
 *
 * Out of scope:
 *   - Business validation rules (ValidationService hooks only)
 *   - Login / save progress UIs
 *   - Eye-test store / calendar / maps UI
 *   - Eye-test backend APIs / booking logic
 *   - Lens add-ons pricing / recommendations algorithms
 *   - Cart Drawer Foundation redesign (basari-cart.js)
 *   - VisionCarePro integration (provider TBD)
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), video, [tabindex]:not([tabindex="-1"])';
  var READY_ATTR = 'data-basari-lens-drawer-ready';
  var OPEN_CLASS = 'basari-lens-drawer-open';
  var ACTIVE_CLASS = 'is-active';

  var LAYOUT_PACKAGES = 'packages';
  var LAYOUT_SUBTYPE = 'subtype-packages';
  var LAYOUT_FUTURE = 'future';

  /** Named drawer routes — never use numeric step IDs. */
  var STEP_VISION = 'vision-type';
  var STEP_OPTIONS = 'options';
  var STEP_PRESCRIPTION_HUB = 'prescription-hub';
  var STEP_PRESCRIPTION_METHODS = 'prescription-methods';
  /**
   * TEMPORARY (remove in Sprint 1B.16B / 1B.16C):
   * Legacy merchant destination from Theme Editor / product.json.
   * Canonical entry route is STEP_EYE_TEST_STORE only.
   * Alias resolution lives solely in resolveEyeTestRouteAlias() — do not re-check elsewhere.
   */
  var STEP_EYE_TEST_BOOKING_LEGACY = 'eye-test-booking';
  var STEP_EYE_TEST_STORE = 'eye-test-store';
  var STEP_EYE_TEST_DATE_TIME = 'eye-test-date-time';
  var STEP_EYE_TEST_CONFIRMATION = 'eye-test-confirmation';
  var STEP_MANUAL_PRESCRIPTION = 'manual-prescription';
  var STEP_ACCOUNT_PRESCRIPTION = 'account-prescription';
  var STEP_UPLOAD_PRESCRIPTION = 'upload-prescription';
  var STEP_SAVE_FOR_LATER = 'save-for-later';
  var STEP_LENS_ADDONS = 'lens-addons';

  var PLACEHOLDER_STEPS = {};
  PLACEHOLDER_STEPS[STEP_ACCOUNT_PRESCRIPTION] = true;
  PLACEHOLDER_STEPS[STEP_SAVE_FOR_LATER] = true;

  /**
   * Method-child routes — back navigates to prescription-methods.
   * Manual + Upload are registered steps; others remain placeholders.
   */
  var METHOD_CHILD_STEPS = {};
  METHOD_CHILD_STEPS[STEP_MANUAL_PRESCRIPTION] = true;
  METHOD_CHILD_STEPS[STEP_ACCOUNT_PRESCRIPTION] = true;
  METHOD_CHILD_STEPS[STEP_UPLOAD_PRESCRIPTION] = true;
  METHOD_CHILD_STEPS[STEP_SAVE_FOR_LATER] = true;

  /**
   * Eye Test Booking canonical routes (1B.16A) — real panels; back uses router history.
   * Legacy `eye-test-booking` is NOT listed here; it is rewritten before routing.
   */
  var EYE_TEST_STEPS = {};
  EYE_TEST_STEPS[STEP_EYE_TEST_STORE] = true;
  EYE_TEST_STEPS[STEP_EYE_TEST_DATE_TIME] = true;
  EYE_TEST_STEPS[STEP_EYE_TEST_CONFIRMATION] = true;

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (node) {
      return (
        !node.hasAttribute('disabled') &&
        node.getAttribute('aria-hidden') !== 'true' &&
        !node.hasAttribute('hidden') &&
        node.offsetParent !== null
      );
    });
  }

  function getAnimationMs(drawer) {
    if (!drawer || !window.getComputedStyle) return 300;
    var raw = window.getComputedStyle(drawer).getPropertyValue('--basari-lens-animation-ms').trim();
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 300 : parsed;
  }

  function lockScroll(lock) {
    document.documentElement.classList.toggle(OPEN_CLASS, lock);
    document.body.classList.toggle(OPEN_CLASS, lock);
  }

  function normalizeKey(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, '_');
  }

  function resolveLayout(visionType) {
    var key = normalizeKey(visionType);

    if (key === 'single_vision' || key === 'singlevision') {
      return LAYOUT_PACKAGES;
    }

    if (
      key === 'progressive' ||
      key === 'bifocal' ||
      key === 'progressive_bifocal' ||
      key === 'progressive_vision_bifocal' ||
      key.indexOf('progressive') === 0 ||
      key.indexOf('bifocal') === 0
    ) {
      return LAYOUT_SUBTYPE;
    }

    if (key === 'zero_power' || key === 'zeropower' || key === 'frame_only' || key === 'frameonly') {
      return LAYOUT_FUTURE;
    }

    return LAYOUT_FUTURE;
  }

  function formatMoney(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    var code = currency || 'INR';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return String(amount);
    }
  }

  function setHidden(node, hidden) {
    if (!node) return;
    if (hidden) {
      node.setAttribute('hidden', '');
    } else {
      node.removeAttribute('hidden');
    }
  }

  /**
   * TEMPORARY legacy alias (Sprint 1B.16A only — remove in 1B.16B / 1B.16C).
   * Single choke-point: rewrite `eye-test-booking` → `eye-test-store`.
   * Call only from navigate(); never from renderStep / goBack / isEyeTestStep.
   * @param {string} stepName
   * @returns {string}
   */
  function resolveEyeTestRouteAlias(stepName) {
    var step = String(stepName || '').trim();
    if (step === STEP_EYE_TEST_BOOKING_LEGACY) {
      return STEP_EYE_TEST_STORE;
    }
    return step;
  }

  function isEyeTestStep(stepName) {
    return !!EYE_TEST_STEPS[String(stepName || '').trim()];
  }

  function isPlaceholderStep(stepName) {
    if (!stepName) return false;
    if (stepName === STEP_MANUAL_PRESCRIPTION) return false;
    if (stepName === STEP_UPLOAD_PRESCRIPTION) return false;
    if (stepName === STEP_LENS_ADDONS) return false;
    if (isEyeTestStep(stepName)) return false;
    if (PLACEHOLDER_STEPS[stepName]) return true;
    return (
      stepName !== STEP_VISION &&
      stepName !== STEP_OPTIONS &&
      stepName !== STEP_PRESCRIPTION_HUB &&
      stepName !== STEP_PRESCRIPTION_METHODS &&
      stepName !== STEP_MANUAL_PRESCRIPTION &&
      stepName !== STEP_UPLOAD_PRESCRIPTION &&
      stepName !== STEP_LENS_ADDONS &&
      !isEyeTestStep(stepName)
    );
  }

  function createEmptyManualPrescription() {
    if (window.BasariPrescription && typeof window.BasariPrescription.createNormalizedPrescription === 'function') {
      return window.BasariPrescription.createNormalizedPrescription({
        source: window.BasariPrescription.SOURCES
          ? window.BasariPrescription.SOURCES.MANUAL
          : 'manual'
      });
    }

    return {
      id: null,
      source: 'manual',
      patientName: '',
      rightEye: { sphere: '', cylinder: '', axis: '', add: '' },
      leftEye: { sphere: '', cylinder: '', axis: '', add: '' },
      pdMode: 'single',
      pd: { single: '', right: '', left: '' },
      notes: '',
      dob: '',
      gender: '',
      meta: {},
      validation: { status: 'idle', errors: {}, sections: {} },
      updatedAt: null
    };
  }

  function createEmptyUploadPrescription() {
    if (window.BasariPrescription && typeof window.BasariPrescription.createUploadPrescription === 'function') {
      return window.BasariPrescription.createUploadPrescription();
    }

    return {
      patientName: '',
      selectedFile: null,
      fileName: '',
      fileSize: 0,
      mimeType: '',
      uploadProgress: 0,
      uploadStatus: 'idle',
      additionalInformation: '',
      dob: '',
      gender: '',
      confirmed: false,
      validation: { valid: true, errors: {}, message: '' },
      updatedAt: null
    };
  }

  function createEmptyLensAddons() {
    if (window.BasariLensAddons && typeof window.BasariLensAddons.createLensAddonsState === 'function') {
      return window.BasariLensAddons.createLensAddonsState();
    }

    return {
      selectedOptions: [],
      expandedCards: [],
      selectedTintColor: null,
      selectedLensIndex: null,
      totalPrice: 0,
      recommendationState: {
        recommendedOptions: [],
        recommendedLensIndex: null,
        recommendationReason: null
      },
      recommendedOptions: [],
      recommendedLensIndex: null,
      recommendationReason: null,
      loadingState: 'idle',
      accessibility: {
        focusReturnSelector: null,
        initialFocusSelector: null,
        keyboardNavigationEnabled: true,
        ariaLivePolite: '',
        labelledBy: null,
        describedBy: null
      },
      meta: {},
      updatedAt: null
    };
  }

  function createEmptyEyeTestBooking() {
    if (
      window.BasariEyeTestBooking &&
      typeof window.BasariEyeTestBooking.createEyeTestBookingState === 'function'
    ) {
      return window.BasariEyeTestBooking.createEyeTestBookingState();
    }

    return {
      enabled: false,
      selectedStore: null,
      selectedDate: null,
      selectedTime: null,
      selectedMonth: null,
      selectedYear: null,
      bookingReference: null,
      status: 'idle',
      metadata: {}
    };
  }

  function BasariLensDrawer(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-basari-lens-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('[data-basari-lens-content]');
    this.overlay = this.drawer.querySelector('[data-basari-lens-overlay]');
    this.closeBtn = this.drawer.querySelector('[data-basari-lens-close]');
    this.backBtn = this.drawer.querySelector('[data-basari-lens-back]');
    this.titleEl = this.drawer.querySelector('[data-basari-lens-title]');
    this.totalBar = this.drawer.querySelector('[data-basari-lens-total]');
    this.visionStep = this.drawer.querySelector('[data-basari-lens-step="vision-type"]');
    this.visionList = this.drawer.querySelector('[data-basari-vision-list]');
    this.visionPreview = this.drawer.querySelector('[data-basari-vision-preview]');
    this.visionPreviewOpen = false;
    this.visionPreviewTrigger = null;
    this.optionsStep = this.drawer.querySelector('[data-basari-lens-options]');
    this.prescriptionHubStep = this.drawer.querySelector('[data-basari-lens-prescription-hub]');
    this.prescriptionMethodsStep = this.drawer.querySelector('[data-basari-lens-prescription-methods]');
    this.manualPrescriptionStep = this.drawer.querySelector('[data-basari-lens-manual-prescription]');
    this.uploadPrescriptionStep = this.drawer.querySelector('[data-basari-lens-upload-prescription]');
    this.lensAddonsStep = this.drawer.querySelector('[data-basari-lens-addons]');
    this.eyeTestStoreStep = this.drawer.querySelector('[data-basari-lens-eye-test-store]');
    this.eyeTestDateTimeStep = this.drawer.querySelector('[data-basari-lens-eye-test-date-time]');
    this.eyeTestConfirmationStep = this.drawer.querySelector('[data-basari-lens-eye-test-confirmation]');
    this.placeholderStep = this.drawer.querySelector('[data-basari-lens-step-placeholder]');
    this.placeholderTitleEl = this.drawer.querySelector('[data-basari-lens-placeholder-title]');
    this.placeholderMessageEl = this.drawer.querySelector('[data-basari-lens-placeholder-message]');
    this.subtypeEl = this.drawer.querySelector('[data-basari-lens-subtype]');
    this.packagesEl = this.drawer.querySelector('[data-basari-lens-packages]');
    this.futureEl = this.drawer.querySelector('[data-basari-lens-future]');
    this.packageItems = this.drawer.querySelectorAll('[data-basari-lens-package-item]');
    this.openBtns = root.querySelectorAll('[data-basari-open-lens-flow]');
    this._confirmBusy = false;

    this.titleVision = this.drawer.getAttribute('data-title-vision') || (this.titleEl ? this.titleEl.textContent : '');
    this.titleOptions = this.drawer.getAttribute('data-title-options') || this.titleVision;
    this.titlePrescription =
      this.drawer.getAttribute('data-title-prescription') || this.titleOptions || this.titleVision;
    this.titleMethods =
      this.drawer.getAttribute('data-title-prescription-methods') || this.titlePrescription;
    this.titleManual =
      this.drawer.getAttribute('data-title-manual-prescription') ||
      this.drawer.getAttribute('data-placeholder-manual') ||
      this.titleMethods;
    this.titleUpload =
      this.drawer.getAttribute('data-title-upload-prescription') ||
      this.drawer.getAttribute('data-placeholder-upload') ||
      this.titleMethods;
    this.titleLensAddons =
      this.drawer.getAttribute('data-title-lens-addons') ||
      this.drawer.getAttribute('data-placeholder-lens-addons') ||
      this.titleMethods;
    this.titleEyeTest =
      this.drawer.getAttribute('data-title-eye-test') ||
      this.drawer.getAttribute('data-placeholder-eye-test') ||
      this.titlePrescription;
    this.titleEyeTestStore =
      this.drawer.getAttribute('data-title-eye-test-store') || this.titleEyeTest;
    this.titleEyeTestDateTime =
      this.drawer.getAttribute('data-title-eye-test-date-time') || this.titleEyeTest;
    this.titleEyeTestConfirmation =
      this.drawer.getAttribute('data-title-eye-test-confirmation') || this.titleEyeTest;

    this.placeholderLabels = {};
    this.placeholderLabels[STEP_MANUAL_PRESCRIPTION] =
      this.drawer.getAttribute('data-placeholder-manual') || 'Manual Prescription';
    this.placeholderLabels[STEP_ACCOUNT_PRESCRIPTION] =
      this.drawer.getAttribute('data-placeholder-account') || 'Account Prescription';
    this.placeholderLabels[STEP_UPLOAD_PRESCRIPTION] =
      this.drawer.getAttribute('data-placeholder-upload') || 'Upload Prescription';
    this.placeholderLabels[STEP_SAVE_FOR_LATER] =
      this.drawer.getAttribute('data-placeholder-later') || 'Save Progress';
    this.placeholderLabels[STEP_EYE_TEST_BOOKING_LEGACY] =
      this.drawer.getAttribute('data-placeholder-eye-test') || 'Eye Test Booking';
    this.placeholderLabels[STEP_EYE_TEST_STORE] = this.titleEyeTestStore;
    this.placeholderLabels[STEP_EYE_TEST_DATE_TIME] = this.titleEyeTestDateTime;
    this.placeholderLabels[STEP_EYE_TEST_CONFIRMATION] = this.titleEyeTestConfirmation;
    this.placeholderLabels[STEP_LENS_ADDONS] =
      this.drawer.getAttribute('data-placeholder-lens-addons') || 'Lens Add-ons';

    /**
     * Router history stack — used for Lens Add-ons / Eye Test back navigation.
     * Existing steps keep hard-coded stack rules; history is consulted first for those routes.
     */
    this.routeHistory = [];

    /**
     * lensFlow — shared selection state for the lens journey.
     * Aliased as this.state for backwards compatibility with 1B.8 / 1B.9.
     */
    this.lensFlow = {
      visionType: null,
      progressiveSubtype: null,
      selectedPackage: null,
      prescriptionChoice: null,
      prescriptionMethod: null,
      /**
       * Normalized prescription object (manual source for 1B.12).
       * Future upload / provider / account flows map into the same shape.
       */
      manualPrescription: createEmptyManualPrescription(),
      /**
       * Upload-specific state branch (1B.13 Phase A).
       * Does not duplicate product, pricing, lens package, or cart data.
       */
      uploadPrescription: createEmptyUploadPrescription(),
      /**
       * Lens Add-ons selection state (1B.14 Phase A).
       * Pricing / recommendations remain placeholders until later phases.
       */
      lensAddons: createEmptyLensAddons(),
      /**
       * Eye Test Booking state (1B.16A).
       * Store / date / time / booking data stay null until later sprints.
       */
      eyeTestBooking: createEmptyEyeTestBooking(),
      currentStep: STEP_VISION,
      /* Internal helpers used by options layout + total bar */
      layout: '',
      subtype: 'progressive',
      packageValue: '',
      packagePrice: 0,
      packageMoney: ''
    };
    this.state = this.lensFlow;

    /* Track product identity for eye-test booking reset on product change */
    this._eyeTestProductKey = this.resolveProductKey();

    /* Prescription Framework — created lazily on manual-prescription enter */
    this.prescriptionFramework = null;
    this.prescriptionController = null;
    this.prescriptionService = null;
    this.prescriptionUploadService = null;
    this.prescriptionFileValidator = null;
    this.prescriptionProvider = null;
    /* Manual Prescription UI — presentation layer only (Phase B) */
    this.manualPrescriptionForm = null;
    /* Upload Prescription UI — presentation layer only (1B.13 Phase B) */
    this.uploadPrescriptionForm = null;
    /* Lens Add-ons Framework — architecture (1B.14 Phase A) + UI (Phase B) */
    this.lensAddonsFramework = null;
    this.lensAddonsController = null;
    this.lensAddonsService = null;
    this.lensAddonsForm = null;
    /* Eye Test Booking Framework — architecture only (1B.16A); script loaded on demand */
    this.eyeTestFramework = null;
    this.eyeTestController = null;
    this.eyeTestStoreService = null;
    this.eyeTestAvailabilityService = null;
    this.eyeTestBookingService = null;
    this.eyeTestCalendarService = null;
    this.eyeTestStoreUI = null;
    this.eyeTestDateTimeUI = null;
    this.eyeTestConfirmationUI = null;
    /* In-flight script load for basari-eye-test-booking.js (instance-scoped; not a global) */
    this._eyeTestScriptPromise = null;

    this.lastFocus = null;
    this.closing = false;
    this.boundKeydown = this.onKeydown.bind(this);

    if (this.panel && !this.panel.hasAttribute('tabindex')) {
      this.panel.setAttribute('tabindex', '-1');
    }

    this.bindTriggers();
    this.bindClose();
    this.bindOptions();
    this.resetToVisionStep(false);
  }

  BasariLensDrawer.prototype.bindTriggers = function () {
    var self = this;
    var drawerId = this.drawer.id || '';

    Array.prototype.forEach.call(this.openBtns, function (btn) {
      if (drawerId) {
        btn.setAttribute('aria-controls', drawerId);
      }
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', function (event) {
        event.preventDefault();
        self.open(btn);
      });
    });
  };

  BasariLensDrawer.prototype.bindClose = function () {
    var self = this;

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        self.close();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.close();
      });
    }

    if (this.backBtn) {
      this.backBtn.addEventListener('click', function (event) {
        event.preventDefault();
        self.goBack();
      });
    }
  };

  BasariLensDrawer.prototype.bindOptions = function () {
    var self = this;

    this.drawer.addEventListener('click', function (event) {
      var videoTrigger = event.target.closest('[data-basari-vision-video-trigger]');
      if (videoTrigger && self.drawer.contains(videoTrigger)) {
        event.preventDefault();
        event.stopPropagation();
        self.openVisionVideoPreview(videoTrigger);
        return;
      }

      var previewNav = event.target.closest('[data-basari-vision-preview-nav]');
      if (previewNav && self.drawer.contains(previewNav)) {
        event.preventDefault();
        event.stopPropagation();
        self.navigateVisionPreview(previewNav);
        return;
      }

      var visionCard = event.target.closest('[data-basari-vision-card]');
      if (visionCard && self.drawer.contains(visionCard)) {
        event.preventDefault();
        self.selectVisionType(visionCard);
        return;
      }

      var subtypeBtn = event.target.closest('[data-basari-lens-subtype-option]');
      if (subtypeBtn && self.drawer.contains(subtypeBtn)) {
        event.preventDefault();
        self.selectSubtype(subtypeBtn.getAttribute('data-subtype') || 'progressive');
        return;
      }

      var packageBtn = event.target.closest('[data-basari-lens-package]');
      if (packageBtn && self.drawer.contains(packageBtn)) {
        event.preventDefault();
        self.selectPackage(packageBtn);
        return;
      }

      var prescriptionCard = event.target.closest('[data-basari-prescription-card]');
      if (prescriptionCard && self.drawer.contains(prescriptionCard)) {
        event.preventDefault();
        self.selectPrescriptionCard(prescriptionCard);
        return;
      }

      var methodCard = event.target.closest('[data-basari-method-card]');
      if (methodCard && self.drawer.contains(methodCard)) {
        event.preventDefault();
        self.selectMethodCard(methodCard);
      }
    });
  };

  BasariLensDrawer.prototype.setExpanded = function (expanded) {
    Array.prototype.forEach.call(this.openBtns, function (btn) {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  BasariLensDrawer.prototype.isOpen = function () {
    return this.drawer && this.drawer.classList.contains('is-open');
  };

  BasariLensDrawer.prototype.open = function (trigger) {
    if (!this.drawer || this.isOpen()) return;

    this.closing = false;
    this.lastFocus = trigger || document.activeElement;
    this.syncEyeTestBookingForProductChange();
    this.resetToVisionStep(false);
    this.drawer.hidden = false;
    lockScroll(true);
    this.setExpanded(true);
    document.addEventListener('keydown', this.boundKeydown);

    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.drawer.classList.add('is-open');
        var focusables = getFocusable(self.panel);
        (focusables[0] || self.panel).focus();
      });
    });
  };

  BasariLensDrawer.prototype.close = function () {
    if (!this.drawer || !this.isOpen() || this.closing) return;

    this.closing = true;
    this.drawer.classList.remove('is-open');
    lockScroll(false);
    this.setExpanded(false);
    document.removeEventListener('keydown', this.boundKeydown);

    var self = this;
    var settled = false;
    var delay = getAnimationMs(this.drawer) + 40;

    function finish() {
      if (settled) return;
      settled = true;
      self.drawer.hidden = true;
      self.closing = false;
      self.resetToVisionStep(false);
      if (self.lastFocus && typeof self.lastFocus.focus === 'function') {
        self.lastFocus.focus();
      }
    }

    function onTransitionEnd(event) {
      if (!self.panel || event.target !== self.panel) return;
      self.panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    }

    if (this.panel) {
      this.panel.addEventListener('transitionend', onTransitionEnd);
    }
    window.setTimeout(finish, delay);
  };

  BasariLensDrawer.prototype.onKeydown = function (event) {
    if (!this.isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.visionPreviewOpen) {
        this.closeVisionVideoPreview({ restoreFocus: true });
        return;
      }
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.panel) return;

    var focusables = getFocusable(this.panel);
    if (!focusables.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  BasariLensDrawer.prototype.setTitle = function (text) {
    if (this.titleEl && text) {
      this.titleEl.textContent = text;
    }
  };

  /**
   * Navigate to a named drawer step.
   * @param {string} stepName - Named route (e.g. "prescription-methods")
   * @param {Object} [options]
   * @param {boolean} [options.focus=true] - Move focus into the step
   * @param {boolean} [options.emit=true] - Dispatch basari:lens:step
   * @param {boolean} [options.silent=false] - Skip event emission
   */
  BasariLensDrawer.prototype.navigate = function (stepName, options) {
    options = options || {};
    /* Legacy eye-test-booking → eye-test-store (sole alias choke-point). */
    var next = resolveEyeTestRouteAlias(String(stepName || '').trim());
    if (!next) return;

    var previous = this.lensFlow.currentStep;

    /* Push history unless this navigate is itself a back/replace transition. */
    if (
      options.back !== true &&
      options.replace !== true &&
      previous &&
      previous !== next
    ) {
      this.routeHistory.push(previous);
    }

    this.lensFlow.currentStep = next;

    this.renderStep(next, options);

    if (options.silent !== true && options.emit !== false) {
      this.emit('basari:lens:step', {
        step: next,
        previousStep: previous,
        visionType: this.lensFlow.visionType,
        layout: this.lensFlow.layout,
        prescriptionChoice: this.lensFlow.prescriptionChoice,
        prescriptionMethod: this.lensFlow.prescriptionMethod,
        selectedPackage: this.lensFlow.selectedPackage
      });
    }
  };

  /**
   * Show / hide step panels for the given named route.
   * Does not mutate lensFlow.currentStep — call navigate() for that.
   * @param {string} stepName
   * @param {Object} [options]
   * @param {boolean} [options.focus=true]
   */
  BasariLensDrawer.prototype.renderStep = function (stepName, options) {
    options = options || {};
    /* Expect canonical route ids — legacy alias is resolved only in navigate(). */
    var step = String(stepName || '').trim();
    var isVision = step === STEP_VISION;
    var isOptions = step === STEP_OPTIONS;
    var isHub = step === STEP_PRESCRIPTION_HUB;
    var isMethods = step === STEP_PRESCRIPTION_METHODS;
    var isManual = step === STEP_MANUAL_PRESCRIPTION;
    var isUpload = step === STEP_UPLOAD_PRESCRIPTION;
    var isLensAddons = step === STEP_LENS_ADDONS;
    var isEyeTestStore = step === STEP_EYE_TEST_STORE;
    var isEyeTestDateTime = step === STEP_EYE_TEST_DATE_TIME;
    var isEyeTestConfirmation = step === STEP_EYE_TEST_CONFIRMATION;
    var isEyeTest = isEyeTestStore || isEyeTestDateTime || isEyeTestConfirmation;
    var isPlaceholder =
      !isManual && !isUpload && !isLensAddons && !isEyeTest && isPlaceholderStep(step);

    setHidden(this.visionStep, !isVision);
    setHidden(this.optionsStep, !isOptions);
    setHidden(this.prescriptionHubStep, !isHub);
    setHidden(this.prescriptionMethodsStep, !isMethods);
    setHidden(this.manualPrescriptionStep, !isManual);
    setHidden(this.uploadPrescriptionStep, !isUpload);
    setHidden(this.lensAddonsStep, !isLensAddons);
    setHidden(this.eyeTestStoreStep, !isEyeTestStore);
    setHidden(this.eyeTestDateTimeStep, !isEyeTestDateTime);
    setHidden(this.eyeTestConfirmationStep, !isEyeTestConfirmation);
    setHidden(this.placeholderStep, !isPlaceholder);
    /* 1B.16D — confirmation is final step: hide Back (routing intact). */
    setHidden(this.backBtn, isVision || isEyeTestConfirmation);

    var hideTotal = isVision || (isOptions && this.lensFlow.layout === LAYOUT_FUTURE);
    setHidden(this.totalBar, hideTotal);

    if (isVision) {
      this.setTitle(this.titleVision);
    } else if (isOptions) {
      this.setTitle(this.titleOptions);
    } else if (isHub) {
      this.setTitle(this.titlePrescription);
    } else if (isMethods) {
      this.setTitle(this.titleMethods);
    } else if (isManual) {
      this.setTitle(this.titleManual);
      this.ensurePrescriptionFoundation();
      this.ensureManualPrescriptionForm();
    } else if (isUpload) {
      this.setTitle(this.titleUpload);
      this.ensurePrescriptionFoundation();
      this.ensureUploadPrescriptionForm();
    } else if (isLensAddons) {
      this.setTitle(this.titleLensAddons);
      this.ensureLensAddonsFoundation();
      this.ensureLensAddonsForm();
      if (this.lensAddonsController && typeof this.lensAddonsController.open === 'function') {
        this.lensAddonsController.open({
          accessibility: {
            labelledBy: this.titleEl ? this.titleEl.id : null,
            initialFocusSelector: '[data-basari-lens-addons-root]'
          }
        });
      }
    } else if (isEyeTest) {
      if (isEyeTestStore) {
        this.setTitle(this.titleEyeTestStore);
      } else if (isEyeTestDateTime) {
        this.setTitle(this.titleEyeTestDateTime);
      } else {
        this.setTitle(this.titleEyeTestConfirmation);
      }
      var eyeTestRoute = step;
      var selfEyeTest = this;
      this.ensureEyeTestFoundation(function (controller) {
        if (!controller || selfEyeTest.lensFlow.currentStep !== eyeTestRoute) return;
        if (typeof controller.open === 'function') {
          try {
            controller.open({ route: eyeTestRoute });
          } catch (error) {
            /* Controller open must not block panel visibility or Store UI mount. */
          }
        }
        /* 1B.16B — mount Store Selection UI once foundation is ready. */
        if (eyeTestRoute === STEP_EYE_TEST_STORE) {
          selfEyeTest.ensureEyeTestStoreUI();
        }
        /* 1B.16C — mount Date & Time Selection UI once foundation is ready. */
        if (eyeTestRoute === STEP_EYE_TEST_DATE_TIME) {
          selfEyeTest.ensureEyeTestDateTimeUI();
        }
        /* 1B.16D — mount Booking Confirmation UI once foundation is ready. */
        if (eyeTestRoute === STEP_EYE_TEST_CONFIRMATION) {
          selfEyeTest.ensureEyeTestConfirmationUI();
        }
      });
    } else if (isPlaceholder) {
      this.renderPlaceholder(step);
      this.setTitle(METHOD_CHILD_STEPS[step] ? this.titleMethods : this.titlePrescription);
    }

    if (isOptions && this.lensFlow.layout) {
      this.applyLayout(this.lensFlow.layout);
    }

    if (options.focus === false || !this.isOpen()) return;

    var focusTarget = null;
    if (isManual) {
      focusTarget =
        (this.manualPrescriptionStep &&
          this.manualPrescriptionStep.querySelector(
            '[data-basari-rx-field="patientName"], input, select, textarea, button:not([data-basari-lens-back])'
          )) ||
        this.backBtn ||
        this.panel;
    } else if (isUpload) {
      focusTarget =
        (this.uploadPrescriptionStep &&
          this.uploadPrescriptionStep.querySelector(
            '[data-basari-rx-field="patientName"], [data-basari-rx-upload-dropzone], input, select, textarea, button:not([data-basari-lens-back])'
          )) ||
        this.backBtn ||
        this.panel;
    } else if (isLensAddons) {
      focusTarget =
        (this.lensAddonsStep &&
          this.lensAddonsStep.querySelector(
            '[data-basari-lens-addon-toggle], [data-basari-lens-addons-root], button:not([data-basari-lens-back]), [tabindex]:not([tabindex="-1"])'
          )) ||
        this.backBtn ||
        this.panel;
    } else if (isEyeTestStore) {
      focusTarget =
        (this.eyeTestStoreStep &&
          this.eyeTestStoreStep.querySelector(
            '[data-basari-eye-test-store-search], [data-basari-eye-test-store-item], [data-basari-lens-eye-test-store-root], button:not([data-basari-lens-back]), [tabindex]:not([tabindex="-1"])'
          )) ||
        this.backBtn ||
        this.panel;
    } else if (isEyeTestDateTime) {
      focusTarget =
        (this.eyeTestDateTimeStep &&
          this.eyeTestDateTimeStep.querySelector(
            '[data-basari-eye-test-day]:not([disabled]), [data-basari-eye-test-month-next], [data-basari-lens-eye-test-date-time-root], button:not([data-basari-lens-back]), [tabindex]:not([tabindex="-1"])'
          )) ||
        this.backBtn ||
        this.panel;
    } else if (isEyeTestConfirmation) {
      focusTarget =
        (this.eyeTestConfirmationStep &&
          this.eyeTestConfirmationStep.querySelector(
            '[data-basari-lens-eye-test-confirmation-root], button:not([data-basari-lens-back]), [tabindex]:not([tabindex="-1"])'
          )) ||
        this.backBtn ||
        this.panel;
    } else if (isMethods) {
      focusTarget = this.drawer.querySelector('[data-basari-method-card]') || this.backBtn;
    } else if (isHub) {
      focusTarget = this.drawer.querySelector('[data-basari-prescription-card]') || this.backBtn;
    } else if (isPlaceholder) {
      focusTarget = this.backBtn || this.panel;
    } else if (isOptions) {
      focusTarget =
        (this.lensFlow.layout === LAYOUT_SUBTYPE &&
          this.subtypeEl &&
          this.subtypeEl.querySelector('[data-basari-lens-subtype-option]')) ||
        this.drawer.querySelector('[data-basari-lens-package-item]:not([hidden]) [data-basari-lens-package]') ||
        this.backBtn ||
        this.panel;
    } else if (isVision) {
      var focusables = getFocusable(this.panel);
      focusTarget = focusables[0] || this.panel;
    }

    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  };

  /**
   * Update placeholder panel copy for a future named route.
   * @param {string} stepName
   */
  BasariLensDrawer.prototype.renderPlaceholder = function (stepName) {
    if (!this.placeholderStep) return;

    this.placeholderStep.setAttribute('data-placeholder-step', stepName);

    var label = this.placeholderLabels[stepName] || stepName.replace(/[-_]+/g, ' ');
    if (this.placeholderTitleEl) {
      this.placeholderTitleEl.textContent = label;
    }
    if (this.placeholderMessageEl) {
      var existing = this.placeholderMessageEl.getAttribute('data-default-message');
      if (!existing) {
        this.placeholderMessageEl.setAttribute('data-default-message', this.placeholderMessageEl.textContent || '');
      } else {
        this.placeholderMessageEl.textContent = existing;
      }
    }
  };

  /**
   * Stack-aware back navigation.
   * lens-addons → previous prescription step (router history)
   * eye-test-* → previous eye-test / hub step (router history; booking state preserved)
   * method children (manual + upload + placeholders) → prescription-methods
   * prescription-methods → prescription-hub
   * prescription-hub → options
   * options → vision-type (full reset)
   */
  BasariLensDrawer.prototype.goBack = function () {
    if (this.visionPreviewOpen) {
      this.closeVisionVideoPreview({ restoreFocus: true });
      return;
    }

    var step = this.lensFlow.currentStep;

    if (step === STEP_LENS_ADDONS) {
      if (this.lensAddonsController && typeof this.lensAddonsController.close === 'function') {
        this.lensAddonsController.close();
      }
      var previousAddons = this.routeHistory.length ? this.routeHistory.pop() : null;
      if (!previousAddons) {
        previousAddons = this.resolvePrescriptionReturnStep();
      }
      this.navigate(previousAddons, { back: true });
      return;
    }

    if (isEyeTestStep(step)) {
      /* Preserve eyeTestBooking during back navigation — do not reset here. */
      var previousEyeTest = this.routeHistory.length ? this.routeHistory.pop() : null;
      if (!previousEyeTest) {
        if (step === STEP_EYE_TEST_CONFIRMATION) {
          previousEyeTest = STEP_EYE_TEST_DATE_TIME;
        } else if (step === STEP_EYE_TEST_DATE_TIME) {
          previousEyeTest = STEP_EYE_TEST_STORE;
        } else {
          previousEyeTest = STEP_PRESCRIPTION_HUB;
        }
      }
      this.navigate(previousEyeTest, { back: true });
      return;
    }

    if (METHOD_CHILD_STEPS[step]) {
      this.lensFlow.prescriptionMethod = null;
      this.syncMethodCards(null);
      this.navigate(STEP_PRESCRIPTION_METHODS, { back: true });
      return;
    }

    if (isPlaceholderStep(step)) {
      this.navigate(STEP_PRESCRIPTION_HUB, { back: true });
      return;
    }

    if (step === STEP_PRESCRIPTION_METHODS) {
      this.lensFlow.prescriptionMethod = null;
      this.syncMethodCards(null);
      this.navigate(STEP_PRESCRIPTION_HUB, { back: true });
      return;
    }

    if (step === STEP_PRESCRIPTION_HUB) {
      this.lensFlow.prescriptionChoice = null;
      this.syncPrescriptionCards(null);
      this.navigate(STEP_OPTIONS, { back: true });
      return;
    }

    if (step === STEP_OPTIONS) {
      this.resetToVisionStep(true);
      return;
    }

    this.close();
  };

  BasariLensDrawer.prototype.resetPackageSelection = function () {
    this.lensFlow.selectedPackage = null;
    this.lensFlow.packageValue = '';
    this.lensFlow.packagePrice = 0;
    this.lensFlow.packageMoney = '';

    Array.prototype.forEach.call(this.drawer.querySelectorAll('[data-basari-lens-package]'), function (btn) {
      btn.classList.remove(ACTIVE_CLASS);
      btn.setAttribute('aria-pressed', 'false');
    });

    this.updateTotalBar();
  };

  BasariLensDrawer.prototype.resetPrescriptionChoice = function () {
    this.lensFlow.prescriptionChoice = null;
    this.syncPrescriptionCards(null);
    this.resetPrescriptionMethod();
  };

  BasariLensDrawer.prototype.resetPrescriptionMethod = function () {
    this.lensFlow.prescriptionMethod = null;
    this.syncMethodCards(null);
    this.resetManualPrescriptionState();
    this.resetUploadPrescriptionState();
    this.resetLensAddonsState();
    this.resetEyeTestBookingState();
  };

  /**
   * Reset centralized manual prescription state without tearing down the framework.
   */
  BasariLensDrawer.prototype.resetManualPrescriptionState = function () {
    this.lensFlow.manualPrescription = createEmptyManualPrescription();

    if (this.prescriptionController && typeof this.prescriptionController.reset === 'function') {
      this.prescriptionController.reset();
      if (this.manualPrescriptionForm && typeof this.manualPrescriptionForm.refresh === 'function') {
        this.manualPrescriptionForm.refresh();
      }
      return;
    }

    if (this.prescriptionService && typeof this.prescriptionService.reset === 'function') {
      this.prescriptionService.reset();
    }

    if (this.manualPrescriptionForm && typeof this.manualPrescriptionForm.refresh === 'function') {
      this.manualPrescriptionForm.refresh();
    }
  };

  /**
   * Reset upload prescription state without tearing down the framework.
   * Architecture only — no upload UI refresh in 1B.13 Phase A.
   */
  BasariLensDrawer.prototype.resetUploadPrescriptionState = function () {
    this.lensFlow.uploadPrescription = createEmptyUploadPrescription();

    if (this.prescriptionController && typeof this.prescriptionController.resetUpload === 'function') {
      try {
        this.prescriptionController.resetUpload();
      } catch (error) {
        /* UploadService may not be configured on older framework instances. */
      }
      return;
    }

    if (this.prescriptionUploadService && typeof this.prescriptionUploadService.reset === 'function') {
      this.prescriptionUploadService.reset();
    }
  };

  /**
   * Resolve the prescription step to return to when history is empty.
   * Prefers the active method destination; falls back to methods hub.
   */
  BasariLensDrawer.prototype.resolvePrescriptionReturnStep = function () {
    var method = String(this.lensFlow.prescriptionMethod || '').trim();
    var methodToStep = {};
    methodToStep.manual = STEP_MANUAL_PRESCRIPTION;
    methodToStep.upload = STEP_UPLOAD_PRESCRIPTION;
    methodToStep.account = STEP_ACCOUNT_PRESCRIPTION;
    methodToStep.later = STEP_SAVE_FOR_LATER;
    methodToStep[STEP_MANUAL_PRESCRIPTION] = STEP_MANUAL_PRESCRIPTION;
    methodToStep[STEP_UPLOAD_PRESCRIPTION] = STEP_UPLOAD_PRESCRIPTION;
    methodToStep[STEP_ACCOUNT_PRESCRIPTION] = STEP_ACCOUNT_PRESCRIPTION;
    methodToStep[STEP_SAVE_FOR_LATER] = STEP_SAVE_FOR_LATER;

    if (methodToStep[method]) {
      return methodToStep[method];
    }
    if (METHOD_CHILD_STEPS[method]) {
      return method;
    }
    return STEP_PRESCRIPTION_METHODS;
  };

  /**
   * Reset lens add-ons state without tearing down the framework permanently.
   */
  BasariLensDrawer.prototype.resetLensAddonsState = function () {
    this.lensFlow.lensAddons = createEmptyLensAddons();

    if (this.lensAddonsController && typeof this.lensAddonsController.reset === 'function') {
      try {
        this.lensAddonsController.reset();
      } catch (error) {
        /* Controller may not be initialized yet. */
      }
      if (this.lensAddonsForm && typeof this.lensAddonsForm.refresh === 'function') {
        this.lensAddonsForm.refresh();
      }
      return;
    }

    if (this.lensAddonsService && typeof this.lensAddonsService.reset === 'function') {
      this.lensAddonsService.reset();
    }

    if (this.lensAddonsForm && typeof this.lensAddonsForm.refresh === 'function') {
      this.lensAddonsForm.refresh();
    }
  };

  /**
   * Reset eye-test booking state without tearing down the framework permanently.
   * Called on drawer close, product change, prescription method/choice reset,
   * and vision type change (via resetPrescriptionChoice → resetPrescriptionMethod).
   * Not called during Back navigation within the eye-test flow.
   */
  BasariLensDrawer.prototype.resetEyeTestBookingState = function () {
    this.lensFlow.eyeTestBooking = createEmptyEyeTestBooking();

    if (this.eyeTestController && typeof this.eyeTestController.reset === 'function') {
      try {
        this.eyeTestController.reset();
      } catch (error) {
        /* Controller may not be initialized yet. */
      }
    }
  };

  /**
   * Product identity key — used to reset eye-test booking when the PDP product changes.
   */
  BasariLensDrawer.prototype.resolveProductKey = function () {
    if (!this.root) return '';
    var productId =
      this.root.getAttribute('data-product-id') ||
      this.root.getAttribute('data-basari-product-id') ||
      '';
    var variantId = this.root.getAttribute('data-variant-id') || '';
    return String(productId || '') + ':' + String(variantId || '');
  };

  /**
   * Reset eye-test booking when the underlying product/variant identity changes.
   */
  BasariLensDrawer.prototype.syncEyeTestBookingForProductChange = function () {
    var nextKey = this.resolveProductKey();
    if (this._eyeTestProductKey && nextKey && this._eyeTestProductKey !== nextKey) {
      this.resetEyeTestBookingState();
    }
    this._eyeTestProductKey = nextKey;
  };

  /**
   * Load basari-eye-test-booking.js once when first required.
   * URL comes from drawer data-eye-test-script (no new globals).
   * @param {Function} [onReady]
   */
  BasariLensDrawer.prototype._loadEyeTestScript = function (onReady) {
    var done = typeof onReady === 'function' ? onReady : function () {};

    if (window.BasariEyeTestBooking && typeof window.BasariEyeTestBooking.createFramework === 'function') {
      done(true);
      return;
    }

    if (this._eyeTestScriptPromise) {
      this._eyeTestScriptPromise.then(function (ok) {
        done(!!ok);
      });
      return;
    }

    var src = this.drawer ? this.drawer.getAttribute('data-eye-test-script') : '';
    src = String(src || '').trim();
    if (!src) {
      done(false);
      return;
    }

    var self = this;
    this._eyeTestScriptPromise = new Promise(function (resolve) {
      var existing = document.querySelector('script[data-basari-eye-test-booking-src]');
      if (existing) {
        if (window.BasariEyeTestBooking && typeof window.BasariEyeTestBooking.createFramework === 'function') {
          resolve(true);
          return;
        }
        existing.addEventListener('load', function () {
          resolve(!!(window.BasariEyeTestBooking && window.BasariEyeTestBooking.createFramework));
        });
        existing.addEventListener('error', function () {
          self._eyeTestScriptPromise = null;
          resolve(false);
        });
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-basari-eye-test-booking-src', src);
      script.onload = function () {
        resolve(!!(window.BasariEyeTestBooking && window.BasariEyeTestBooking.createFramework));
      };
      script.onerror = function () {
        self._eyeTestScriptPromise = null;
        resolve(false);
      };
      document.head.appendChild(script);
    });

    this._eyeTestScriptPromise.then(function (ok) {
      done(!!ok);
    });
  };

  /**
   * Lazy-init Eye Test Booking Framework when an eye-test route becomes active.
   * Extends the existing ensure*Foundation pattern: loads the script on demand,
   * then creates the framework once. Architecture only — no UI mount in 1B.16A.
   * @param {Function} [onReady] - Optional callback(controller|null) after init
   * @returns {Object|null} controller when already available; otherwise null pending load
   */
  BasariLensDrawer.prototype.ensureEyeTestFoundation = function (onReady) {
    var self = this;
    var ready = typeof onReady === 'function' ? onReady : null;

    this.syncEyeTestBookingForProductChange();

    function finish(controller) {
      if (ready) ready(controller || null);
      return controller || null;
    }

    if (this.eyeTestController) {
      if (!this.eyeTestController.initialized) {
        if (!this.lensFlow.eyeTestBooking) {
          this.lensFlow.eyeTestBooking = createEmptyEyeTestBooking();
        }
        this.eyeTestController.initialize(this.lensFlow.eyeTestBooking);
      }
      return finish(this.eyeTestController);
    }

    if (!window.BasariEyeTestBooking || typeof window.BasariEyeTestBooking.createFramework !== 'function') {
      this._loadEyeTestScript(function (ok) {
        if (!ok) {
          finish(null);
          return;
        }
        /* Re-enter ensure path once; controller guard prevents duplicate init. */
        self.ensureEyeTestFoundation(ready);
      });
      return null;
    }

    if (!this.lensFlow.eyeTestBooking) {
      this.lensFlow.eyeTestBooking = createEmptyEyeTestBooking();
    }

    var framework = window.BasariEyeTestBooking.createFramework({
      stateRef: this.lensFlow,
      stateKey: 'eyeTestBooking',
      emit: function (name, detail) {
        self.emit(name, detail);
      },
      navigate: function (stepName, opts) {
        self.navigate(stepName, opts);
      },
      goBack: function () {
        self.goBack();
      },
      closeDrawer: function () {
        self.close();
      }
    });

    this.eyeTestFramework = framework;
    this.eyeTestController = framework.controller;
    this.eyeTestStoreService = framework.storeService;
    this.eyeTestAvailabilityService = framework.availabilityService;
    this.eyeTestBookingService = framework.bookingService;
    this.eyeTestCalendarService = framework.calendarService;
    this.eyeTestController.initialize(this.lensFlow.eyeTestBooking);

    return finish(this.eyeTestController);
  };

  /**
   * Lazy-mount Eye Test Store Selection UI inside [data-basari-lens-eye-test-store-root].
   * UI never mutates lensFlow directly — only via EyeTestController.selectStore().
   * Sprint 1B.16B — UI only (mock StoreService; no maps / booking APIs).
   */
  BasariLensDrawer.prototype.ensureEyeTestStoreUI = function () {
    if (this.eyeTestStoreUI) {
      if (typeof this.eyeTestStoreUI.refresh === 'function') {
        this.eyeTestStoreUI.refresh();
      }
      return this.eyeTestStoreUI;
    }

    if (!window.BasariEyeTestStore || typeof window.BasariEyeTestStore.mount !== 'function') {
      return null;
    }

    var root =
      this.eyeTestStoreStep &&
      this.eyeTestStoreStep.querySelector('[data-basari-lens-eye-test-store-root]');
    if (!root) return null;

    var self = this;
    var labels = {
      openNow: this.drawer.getAttribute('data-label-eye-test-store-open') || 'Open Now',
      closed: this.drawer.getAttribute('data-label-eye-test-store-closed') || 'Closed'
    };

    this.eyeTestStoreUI = window.BasariEyeTestStore.mount(root, {
      getController: function () {
        return self.eyeTestController || self.ensureEyeTestFoundation();
      },
      storeService:
        window.BasariEyeTestStore.StoreService
          ? new window.BasariEyeTestStore.StoreService()
          : null,
      labels: labels,
      onClose: function () {
        self.close();
      }
    });

    return this.eyeTestStoreUI;
  };

  /**
   * Lazy-mount Eye Test Date & Time Selection UI inside [data-basari-lens-eye-test-date-time-root].
   * UI never mutates lensFlow directly — only via EyeTestController.selectDate/selectTime/selectMonth.
   * Sprint 1B.16C — UI only (mock AvailabilityService; no booking APIs / calendar).
   */
  BasariLensDrawer.prototype.ensureEyeTestDateTimeUI = function () {
    if (this.eyeTestDateTimeUI) {
      if (typeof this.eyeTestDateTimeUI.refresh === 'function') {
        this.eyeTestDateTimeUI.refresh();
      }
      return this.eyeTestDateTimeUI;
    }

    if (!window.BasariEyeTestDateTime || typeof window.BasariEyeTestDateTime.mount !== 'function') {
      return null;
    }

    var root =
      this.eyeTestDateTimeStep &&
      this.eyeTestDateTimeStep.querySelector('[data-basari-lens-eye-test-date-time-root]');
    if (!root) return null;

    var self = this;

    this.eyeTestDateTimeUI = window.BasariEyeTestDateTime.mount(root, {
      getController: function () {
        return self.eyeTestController || self.ensureEyeTestFoundation();
      },
      availabilityService:
        window.BasariEyeTestDateTime.AvailabilityService
          ? new window.BasariEyeTestDateTime.AvailabilityService()
          : null,
      onClose: function () {
        self.close();
      }
    });

    return this.eyeTestDateTimeUI;
  };

  /**
   * Lazy-mount Eye Test Booking Confirmation UI inside [data-basari-lens-eye-test-confirmation-root].
   * Read-only presentation — never mutates lensFlow; reads via EyeTestController.getState().
   * Sprint 1B.16D — UI only (no booking API / calendar / cart).
   */
  BasariLensDrawer.prototype.ensureEyeTestConfirmationUI = function () {
    if (this.eyeTestConfirmationUI) {
      if (typeof this.eyeTestConfirmationUI.refresh === 'function') {
        this.eyeTestConfirmationUI.refresh();
      }
      return this.eyeTestConfirmationUI;
    }

    if (
      !window.BasariEyeTestConfirmation ||
      typeof window.BasariEyeTestConfirmation.mount !== 'function'
    ) {
      return null;
    }

    var root =
      this.eyeTestConfirmationStep &&
      this.eyeTestConfirmationStep.querySelector('[data-basari-lens-eye-test-confirmation-root]');
    if (!root) return null;

    var self = this;
    var titleEl = root.querySelector('.basari-eye-test-confirmation__title');
    var noticeEl = root.querySelector('[data-basari-eye-test-confirmation-calendar-notice]');
    var labels = {
      openNow: this.drawer.getAttribute('data-label-eye-test-store-open') || 'Open Now',
      closed: this.drawer.getAttribute('data-label-eye-test-store-closed') || 'Closed',
      heading: (titleEl && titleEl.textContent) || this.titleEyeTestConfirmation || 'Booking Completed',
      calendarNotice:
        (noticeEl && noticeEl.textContent && noticeEl.textContent.trim()) ||
        'Calendar integration will be available in a future sprint.'
    };

    this.eyeTestConfirmationUI = window.BasariEyeTestConfirmation.mount(root, {
      getController: function () {
        return self.eyeTestController || self.ensureEyeTestFoundation();
      },
      labels: labels,
      onClose: function () {
        self.close();
      }
    });

    return this.eyeTestConfirmationUI;
  };

  /**
   * Lazy-init Lens Add-ons Framework when lens-addons route becomes active.
   * Architecture only — UI mounts via ensureLensAddonsForm() in Phase B.
   */
  BasariLensDrawer.prototype.ensureLensAddonsFoundation = function () {
    if (this.lensAddonsController) {
      if (!this.lensAddonsController.initialized) {
        if (!this.lensFlow.lensAddons) {
          this.lensFlow.lensAddons = createEmptyLensAddons();
        }
        this.lensAddonsController.initialize(this.lensFlow.lensAddons);
      }
      return this.lensAddonsController;
    }

    if (!window.BasariLensAddons || typeof window.BasariLensAddons.createFramework !== 'function') {
      return null;
    }

    var self = this;

    if (!this.lensFlow.lensAddons) {
      this.lensFlow.lensAddons = createEmptyLensAddons();
    }

    var labels = {
      drawerTitle: this.titleLensAddons,
      confirmLabel: this.drawer.getAttribute('data-label-lens-addons-confirm') || 'Confirm',
      closeLabel: this.drawer.getAttribute('data-label-lens-addons-close') || 'Close',
      totalLabel: this.drawer.getAttribute('data-label-lens-addons-total') || 'Total',
      recommendedLabel: this.drawer.getAttribute('data-label-lens-addons-recommended') || 'Recommended'
    };

    var featureToggles = {
      enableTintSelector: this.drawer.getAttribute('data-toggle-lens-addons-tint') !== 'false',
      enableLensIndexSelector: this.drawer.getAttribute('data-toggle-lens-addons-index') !== 'false',
      enableRecommendations: this.drawer.getAttribute('data-toggle-lens-addons-recommendations') === 'true',
      enablePricing: this.drawer.getAttribute('data-toggle-lens-addons-pricing') === 'true'
    };

    var framework = window.BasariLensAddons.createFramework({
      stateRef: this.lensFlow,
      stateKey: 'lensAddons',
      configuration: {
        labels: labels,
        featureToggles: featureToggles
      },
      emit: function (name, detail) {
        self.emit(name, detail);
      },
      navigate: function (stepName, opts) {
        self.navigate(stepName, opts);
      },
      goBack: function () {
        self.goBack();
      },
      closeDrawer: function () {
        self.close();
      }
    });

    this.lensAddonsFramework = framework;
    this.lensAddonsService = framework.service;
    this.lensAddonsController = framework.controller;
    this.lensAddonsController.initialize(this.lensFlow.lensAddons);

    return this.lensAddonsController;
  };

  /**
   * Lazy-mount Lens Add-ons UI inside [data-basari-lens-addons-root].
   * UI never mutates lensFlow directly — only via LensAddonsController.
   */
  BasariLensDrawer.prototype.ensureLensAddonsForm = function () {
    if (this.lensAddonsForm) {
      if (typeof this.lensAddonsForm.refresh === 'function') {
        this.lensAddonsForm.refresh();
      }
      return this.lensAddonsForm;
    }

    if (!window.BasariLensAddonsForm || typeof window.BasariLensAddonsForm.mount !== 'function') {
      return null;
    }

    var root =
      this.lensAddonsStep && this.lensAddonsStep.querySelector('[data-basari-lens-addons-root]');
    if (!root) return null;

    var self = this;

    this.lensAddonsForm = window.BasariLensAddonsForm.mount(root, {
      getController: function () {
        return self.ensureLensAddonsFoundation();
      },
      eventTarget: this.drawer,
      totalBar: this.totalBar,
      onClose: function () {
        self.close();
      },
      onConfirm: function () {
        self.confirmLensAddonsAndAddToCart();
      }
    });

    return this.lensAddonsForm;
  };

  /**
   * Resolve Dawn cart host (<cart-drawer> or <cart-notification>) — same as product-form.
   */
  BasariLensDrawer.prototype.getDawnCart = function () {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  };

  /**
   * Selected PDP variant id from [data-basari-product] (kept in sync by basari-product-variants).
   */
  BasariLensDrawer.prototype.getSelectedVariantId = function () {
    if (!this.root) return '';
    return String(this.root.getAttribute('data-variant-id') || '').trim();
  };

  /**
   * Build line-item properties from prepared lens selection + lensFlow context.
   * Kept structural — no VisionCarePro / pricing business rules.
   */
  BasariLensDrawer.prototype.buildLensCartProperties = function (selection) {
    var props = {};
    var flow = this.lensFlow || {};
    var sel = selection || {};

    if (flow.visionType) props['Vision Type'] = String(flow.visionType);
    if (flow.progressiveSubtype) props.Subtype = String(flow.progressiveSubtype);
    if (flow.packageValue) props['Lens Package'] = String(flow.packageValue);
    if (flow.prescriptionMethod) props['Prescription Method'] = String(flow.prescriptionMethod);

    if (Array.isArray(sel.selectedOptions) && sel.selectedOptions.length) {
      props['Lens Add-ons'] = sel.selectedOptions.join(', ');
    }
    if (sel.selectedTintColor) props['Tint Color'] = String(sel.selectedTintColor);
    if (sel.selectedLensIndex) props['Lens Index'] = String(sel.selectedLensIndex);

    return props;
  };

  /**
   * Lens Add-ons Confirm → Dawn Product Form cart lifecycle (parity with product-form.js):
   *   1. prepareSelectionState()
   *   2. POST routes.cart_add_url with cart.getSectionsToRender() section ids
   *   3. publish(PUB_SUB_EVENTS.cartUpdate)
   *   4. cart.renderContents(response) → Dawn CartDrawer.open()
   *
   * Does not call cart.open() directly. Does not modify basari-cart.js.
   */
  BasariLensDrawer.prototype.confirmLensAddonsAndAddToCart = function () {
    var self = this;
    if (this._confirmBusy) return;

    var controller = this.ensureLensAddonsFoundation();
    var selection = null;
    if (controller && typeof controller.prepareSelectionState === 'function') {
      selection = controller.prepareSelectionState();
    }

    var variantId = this.getSelectedVariantId();
    if (!variantId) {
      console.error('[BasariLensDrawer] Missing data-variant-id; cannot add to cart.');
      return;
    }

    var cart = this.getDawnCart();
    var routes = window.routes || {};
    var cartAddUrl = routes.cart_add_url || '/cart/add';

    var config =
      typeof window.fetchConfig === 'function'
        ? window.fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'Content-Type': 'application/json' }
          };
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    var properties = this.buildLensCartProperties(selection);
    Object.keys(properties).forEach(function (key) {
      formData.append('properties[' + key + ']', properties[key]);
    });

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    config.body = formData;

    this._confirmBusy = true;
    var confirmBtn =
      this.lensAddonsForm && this.lensAddonsForm.cache
        ? this.lensAddonsForm.cache.confirmBtn
        : this.drawer.querySelector('[data-basari-lens-addons-confirm]');
    if (confirmBtn) {
      confirmBtn.setAttribute('aria-disabled', 'true');
      confirmBtn.setAttribute('disabled', '');
    }

    fetch(cartAddUrl, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response && response.status) {
          console.error('[BasariLensDrawer] Add to cart failed:', response.description || response.message);
          return;
        }

        if (!cart) {
          window.location = routes.cart_url || '/cart';
          return;
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'basari-lens-addons',
            productVariantId: variantId,
            cartData: response
          });
        }

        /* Close lens drawer first (quick-add-modal pattern), then Dawn renderContents → open(). */
        var delay = getAnimationMs(self.drawer) + 40;
        self.close();
        window.setTimeout(function () {
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          if (typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          }
        }, delay);
      })
      .catch(function (error) {
        console.error('[BasariLensDrawer] Add to cart error:', error);
      })
      .finally(function () {
        self._confirmBusy = false;
        if (confirmBtn) {
          confirmBtn.removeAttribute('aria-disabled');
          confirmBtn.removeAttribute('disabled');
        }
      });
  };

  /**
   * Lazy-mount Manual Prescription UI inside [data-basari-prescription-root].
   * UI never mutates lensFlow directly — only via PrescriptionController.
   */
  BasariLensDrawer.prototype.ensureManualPrescriptionForm = function () {
    if (this.manualPrescriptionForm) {
      if (typeof this.manualPrescriptionForm.refresh === 'function') {
        this.manualPrescriptionForm.refresh();
      }
      return this.manualPrescriptionForm;
    }

    if (!window.BasariPrescriptionForm || typeof window.BasariPrescriptionForm.mount !== 'function') {
      return null;
    }

    var root =
      this.manualPrescriptionStep &&
      this.manualPrescriptionStep.querySelector('[data-basari-prescription-root]');
    if (!root) return null;

    var self = this;

    this.manualPrescriptionForm = window.BasariPrescriptionForm.mount(root, {
      getController: function () {
        return self.ensurePrescriptionFoundation();
      },
      eventTarget: this.drawer,
      onClose: function () {
        self.close();
      },
      onConfirm: function () {
        /* 1B.14 Phase A — temporary navigation only (no validation / VCP / cart). */
        var controller = self.ensurePrescriptionFoundation();
        if (controller && typeof controller.proceedToLensAddons === 'function') {
          controller.proceedToLensAddons();
        }
      }
    });

    return this.manualPrescriptionForm;
  };

  /**
   * Lazy-mount Upload Prescription UI inside [data-basari-prescription-root][data-basari-prescription-source="upload"].
   * UI never mutates lensFlow directly — only via PrescriptionController upload APIs.
   */
  BasariLensDrawer.prototype.ensureUploadPrescriptionForm = function () {
    if (this.uploadPrescriptionForm) {
      if (typeof this.uploadPrescriptionForm.refresh === 'function') {
        this.uploadPrescriptionForm.refresh();
      }
      return this.uploadPrescriptionForm;
    }

    if (
      !window.BasariPrescriptionUploadForm ||
      typeof window.BasariPrescriptionUploadForm.mount !== 'function'
    ) {
      return null;
    }

    var root =
      this.uploadPrescriptionStep &&
      this.uploadPrescriptionStep.querySelector('[data-basari-prescription-root]');
    if (!root) return null;

    var self = this;

    this.uploadPrescriptionForm = window.BasariPrescriptionUploadForm.mount(root, {
      getController: function () {
        return self.ensurePrescriptionFoundation();
      },
      eventTarget: this.drawer,
      onClose: function () {
        self.close();
      },
      onConfirm: function () {
        /* 1B.14 Phase A — temporary navigation only (no validation / VCP / cart). */
        var controller = self.ensurePrescriptionFoundation();
        if (controller && typeof controller.proceedToLensAddons === 'function') {
          controller.proceedToLensAddons();
        }
      }
    });

    return this.uploadPrescriptionForm;
  };

  /**
   * Lazy-init Prescription Framework when Manual or Upload Prescription becomes active.
   * Safe to call repeatedly — initializes once per drawer instance.
   * Manual behaviour remains unchanged; UploadService is wired alongside.
   */
  BasariLensDrawer.prototype.ensurePrescriptionFoundation = function () {
    if (this.prescriptionController) {
      if (!this.prescriptionController.initialized) {
        this.prescriptionController.initialize(this.lensFlow.manualPrescription);
      }
      if (
        typeof this.prescriptionController.initializeUpload === 'function' &&
        !this.prescriptionController.uploadInitialized
      ) {
        if (!this.lensFlow.uploadPrescription) {
          this.lensFlow.uploadPrescription = createEmptyUploadPrescription();
        }
        this.prescriptionController.initializeUpload(this.lensFlow.uploadPrescription);
      }
      return this.prescriptionController;
    }

    if (!window.BasariPrescription || typeof window.BasariPrescription.createFramework !== 'function') {
      return null;
    }

    var self = this;

    if (!this.lensFlow.manualPrescription) {
      this.lensFlow.manualPrescription = createEmptyManualPrescription();
    }
    if (!this.lensFlow.uploadPrescription) {
      this.lensFlow.uploadPrescription = createEmptyUploadPrescription();
    }

    var framework = window.BasariPrescription.createFramework({
      providerName: 'mock',
      providerOptions: {
        source: window.BasariPrescription.SOURCES
          ? window.BasariPrescription.SOURCES.MANUAL
          : 'manual'
      },
      stateRef: this.lensFlow,
      stateKey: 'manualPrescription',
      uploadStateKey: 'uploadPrescription',
      emit: function (name, detail) {
        self.emit(name, detail);
      },
      navigate: function (stepName, opts) {
        self.navigate(stepName, opts);
      }
    });

    this.prescriptionFramework = framework;
    this.prescriptionProvider = framework.provider;
    this.prescriptionService = framework.service;
    this.prescriptionUploadService = framework.uploadService || null;
    this.prescriptionFileValidator = framework.fileValidator || null;
    this.prescriptionController = framework.controller;
    this.prescriptionController.initialize(this.lensFlow.manualPrescription);
    if (typeof this.prescriptionController.initializeUpload === 'function') {
      this.prescriptionController.initializeUpload(this.lensFlow.uploadPrescription);
    }

    return this.prescriptionController;
  };

  BasariLensDrawer.prototype.resetToVisionStep = function (focusFirst) {
    if (
      this.lensFlow.currentStep === STEP_LENS_ADDONS &&
      this.lensAddonsController &&
      typeof this.lensAddonsController.close === 'function'
    ) {
      this.lensAddonsController.close();
    }

    this.closeVisionVideoPreview({ restoreFocus: false });

    this.routeHistory = [];
    this.lensFlow.visionType = null;
    this.lensFlow.progressiveSubtype = null;
    this.lensFlow.layout = '';
    this.lensFlow.subtype = 'progressive';
    this.resetPackageSelection();
    this.resetPrescriptionChoice();

    setHidden(this.subtypeEl, true);
    setHidden(this.packagesEl, false);
    setHidden(this.futureEl, true);

    if (this.optionsStep) {
      this.optionsStep.setAttribute('data-vision-type', '');
      this.optionsStep.setAttribute('data-layout', '');
      this.optionsStep.setAttribute('data-subtype', 'progressive');
    }

    this.syncSubtypeButtons('progressive');
    this.filterPackages();

    /* replace: true keeps routeHistory cleared — navigate() must not re-push the prior step. */
    this.navigate(STEP_VISION, {
      focus: !!focusFirst,
      replace: true
    });
  };

  BasariLensDrawer.prototype.selectVisionType = function (card) {
    var visionType = card.getAttribute('data-vision-type') || '';
    var layout = resolveLayout(visionType);

    this.lensFlow.visionType = visionType;
    this.lensFlow.layout = layout;
    this.lensFlow.subtype = 'progressive';
    this.lensFlow.progressiveSubtype = layout === LAYOUT_SUBTYPE ? 'progressive' : null;
    this.resetPackageSelection();
    /*
     * Centralized reset chain — also clears eyeTestBooking via
     * resetPrescriptionChoice → resetPrescriptionMethod → resetEyeTestBookingState.
     * Do not call resetEyeTestBookingState() again here.
     */
    this.resetPrescriptionChoice();

    if (this.optionsStep) {
      this.optionsStep.setAttribute('data-vision-type', visionType);
      this.optionsStep.setAttribute('data-layout', layout);
      this.optionsStep.setAttribute('data-subtype', this.lensFlow.subtype);
    }

    this.syncSubtypeButtons(this.lensFlow.subtype);
    this.filterPackages();

    this.emit('basari:lens:vision-change', {
      visionType: visionType,
      layout: layout
    });

    this.navigate(STEP_OPTIONS);
  };

  BasariLensDrawer.prototype.pauseVisionPreviewVideos = function (scope) {
    var root = scope || this.visionPreview || this.drawer;
    if (!root || !root.querySelectorAll) return;

    Array.prototype.forEach.call(root.querySelectorAll('video'), function (video) {
      try {
        video.pause();
        if (video.currentTime) video.currentTime = 0;
      } catch (error) {
        /* Ignore media errors from paused / unloaded videos. */
      }
    });
  };

  BasariLensDrawer.prototype.openVisionVideoPreview = function (trigger) {
    if (!trigger || !this.visionPreview) return;

    var previewFor = trigger.getAttribute('data-preview-for') || '';
    if (!previewFor) return;

    var panel = this.visionPreview.querySelector(
      '[data-basari-vision-preview-panel][data-preview-for="' + previewFor + '"]'
    );
    if (!panel) return;

    this.pauseVisionPreviewVideos();

    Array.prototype.forEach.call(
      this.visionPreview.querySelectorAll('[data-basari-vision-preview-panel]'),
      function (node) {
        setHidden(node, node !== panel);
      }
    );

    if (this.visionPreviewTrigger && this.visionPreviewTrigger !== trigger) {
      this.visionPreviewTrigger.setAttribute('aria-expanded', 'false');
    }

    this.visionPreviewOpen = true;
    this.visionPreviewTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');

    setHidden(this.visionList, true);
    if (this.visionList) this.visionList.setAttribute('aria-hidden', 'true');
    setHidden(this.visionPreview, false);
    setHidden(this.backBtn, false);
    setHidden(this.totalBar, false);

    var track = panel.querySelector('[data-basari-vision-preview-track]');
    if (track) {
      track.scrollLeft = 0;
      this.bindVisionPreviewTrack(track);
      this.syncVisionPreviewNav(track, 0);
    }

    var video = panel.querySelector('video');
    if (video) {
      video.preload = 'auto';
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    }

    var focusTarget = video || this.backBtn || this.panel;
    if (this.isOpen() && focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  };

  BasariLensDrawer.prototype.bindVisionPreviewTrack = function (track) {
    if (!track || track.getAttribute('data-basari-preview-bound') === 'true') return;

    var self = this;
    track.setAttribute('data-basari-preview-bound', 'true');
    track.addEventListener(
      'scroll',
      function () {
        if (track._basariPreviewRaf) return;
        track._basariPreviewRaf = window.requestAnimationFrame(function () {
          track._basariPreviewRaf = 0;
          self.syncVisionPreviewPlayback(track);
          self.syncVisionPreviewNav(track);
        });
      },
      { passive: true }
    );
  };

  BasariLensDrawer.prototype.getActiveVisionPreviewIndex = function (track) {
    if (!track) return 0;

    var items = track.querySelectorAll('.basari-lens-drawer__vision-preview-item');
    if (!items.length) return 0;

    var trackRect = track.getBoundingClientRect();
    var bestIndex = 0;
    var bestScore = -1;

    Array.prototype.forEach.call(items, function (item, index) {
      var rect = item.getBoundingClientRect();
      var overlap = Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left);
      if (overlap > bestScore) {
        bestScore = overlap;
        bestIndex = index;
      }
    });

    return bestIndex;
  };

  BasariLensDrawer.prototype.syncVisionPreviewNav = function (track, index) {
    if (!track) return;

    var panel = track.closest('[data-basari-vision-preview-panel]');
    if (!panel) return;

    var prevBtn = panel.querySelector('[data-basari-vision-preview-nav="prev"]');
    var nextBtn = panel.querySelector('[data-basari-vision-preview-nav="next"]');
    if (!prevBtn && !nextBtn) return;

    var items = track.querySelectorAll('.basari-lens-drawer__vision-preview-item');
    var activeIndex = typeof index === 'number' ? index : this.getActiveVisionPreviewIndex(track);
    var lastIndex = items.length - 1;

    setHidden(prevBtn, activeIndex <= 0);
    setHidden(nextBtn, activeIndex >= lastIndex);
  };

  BasariLensDrawer.prototype.scrollVisionPreviewTo = function (track, item) {
    if (!track || !item) return;

    if (typeof item.scrollIntoView === 'function') {
      try {
        item.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
        return;
      } catch (error) {
        /* Fall through to native track scrolling. */
      }
    }

    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      return;
    }

    track.scrollLeft = item.offsetLeft;
  };

  BasariLensDrawer.prototype.navigateVisionPreview = function (button) {
    if (!button) return;

    var panel = button.closest('[data-basari-vision-preview-panel]');
    var track = panel && panel.querySelector('[data-basari-vision-preview-track]');
    if (!track) return;

    var items = track.querySelectorAll('.basari-lens-drawer__vision-preview-item');
    if (items.length < 2) return;

    var direction = button.getAttribute('data-basari-vision-preview-nav') || '';
    var currentIndex = this.getActiveVisionPreviewIndex(track);
    var nextIndex = currentIndex;

    if (direction === 'next') {
      nextIndex = currentIndex + 1;
    } else if (direction === 'prev') {
      nextIndex = currentIndex - 1;
    }

    if (nextIndex < 0 || nextIndex >= items.length) return;

    this.scrollVisionPreviewTo(track, items[nextIndex]);
    this.syncVisionPreviewNav(track, nextIndex);
    this.syncVisionPreviewPlayback(track, nextIndex);

    var prevBtn = panel.querySelector('[data-basari-vision-preview-nav="prev"]');
    var nextBtn = panel.querySelector('[data-basari-vision-preview-nav="next"]');
    var focusTarget = null;
    if (nextIndex <= 0) {
      focusTarget = nextBtn && !nextBtn.hasAttribute('hidden') ? nextBtn : button;
    } else if (nextIndex >= items.length - 1) {
      focusTarget = prevBtn && !prevBtn.hasAttribute('hidden') ? prevBtn : button;
    } else {
      focusTarget = button;
    }
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  };

  BasariLensDrawer.prototype.syncVisionPreviewPlayback = function (track, activeIndex) {
    if (!track) return;

    var items = track.querySelectorAll('.basari-lens-drawer__vision-preview-item');
    var index = typeof activeIndex === 'number' ? activeIndex : this.getActiveVisionPreviewIndex(track);
    var best = items[index] || null;

    Array.prototype.forEach.call(track.querySelectorAll('video'), function (video) {
      var inBest = best && best.contains(video);
      if (inBest) {
        video.preload = 'auto';
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
        return;
      }
      try {
        video.pause();
      } catch (error) {
        /* Ignore media errors from off-screen preview videos. */
      }
    });
  };

  BasariLensDrawer.prototype.closeVisionVideoPreview = function (options) {
    options = options || {};
    var wasOpen = this.visionPreviewOpen;
    var trigger = this.visionPreviewTrigger;

    this.pauseVisionPreviewVideos();

    if (this.visionPreview) {
      Array.prototype.forEach.call(
        this.visionPreview.querySelectorAll('[data-basari-vision-preview-panel]'),
        function (node) {
          setHidden(node, true);
        }
      );
      setHidden(this.visionPreview, true);
    }

    setHidden(this.visionList, false);
    if (this.visionList) this.visionList.removeAttribute('aria-hidden');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.visionPreviewOpen = false;
    this.visionPreviewTrigger = null;

    if (this.lensFlow.currentStep === STEP_VISION) {
      setHidden(this.backBtn, true);
      setHidden(this.totalBar, true);
    }

    if (wasOpen && options.restoreFocus !== false && trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  };

  BasariLensDrawer.prototype.applyLayout = function (layout) {
    if (layout === LAYOUT_SUBTYPE) {
      setHidden(this.subtypeEl, false);
      setHidden(this.packagesEl, false);
      setHidden(this.futureEl, true);
      return;
    }

    if (layout === LAYOUT_PACKAGES) {
      setHidden(this.subtypeEl, true);
      setHidden(this.packagesEl, false);
      setHidden(this.futureEl, true);
      return;
    }

    setHidden(this.subtypeEl, true);
    setHidden(this.packagesEl, true);
    setHidden(this.futureEl, false);
  };

  BasariLensDrawer.prototype.syncSubtypeButtons = function (subtype) {
    var buttons = this.drawer.querySelectorAll('[data-basari-lens-subtype-option]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var active = (btn.getAttribute('data-subtype') || '') === subtype;
      btn.classList.toggle(ACTIVE_CLASS, active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  BasariLensDrawer.prototype.selectSubtype = function (subtype) {
    var next = normalizeKey(subtype) === 'bifocal' ? 'bifocal' : 'progressive';
    this.lensFlow.subtype = next;
    this.lensFlow.progressiveSubtype = next;

    if (this.optionsStep) {
      this.optionsStep.setAttribute('data-subtype', next);
    }

    this.syncSubtypeButtons(next);
    this.resetPackageSelection();
    this.filterPackages();

    this.emit('basari:lens:subtype-change', {
      visionType: this.lensFlow.visionType,
      subtype: next
    });
  };

  BasariLensDrawer.prototype.packageMatches = function (item) {
    var applies = normalizeKey(item.getAttribute('data-applies-to') || 'all');
    var subtype = normalizeKey(item.getAttribute('data-subtype') || 'all');
    var vision = normalizeKey(this.lensFlow.visionType);
    var layout = this.lensFlow.layout;

    if (applies !== 'all' && applies !== vision) {
      if (!(layout === LAYOUT_SUBTYPE && applies === 'progressive')) {
        return false;
      }
    }

    if (layout === LAYOUT_SUBTYPE) {
      if (subtype === 'none' || subtype === 'all') return true;
      return subtype === this.lensFlow.subtype;
    }

    return true;
  };

  BasariLensDrawer.prototype.filterPackages = function () {
    var self = this;
    var visibleCount = 0;
    var listEl = this.drawer.querySelector('[data-basari-lens-package-list]');
    var filteredEmpty = this.drawer.querySelector('[data-basari-lens-packages-filtered-empty]');

    Array.prototype.forEach.call(this.packageItems, function (item) {
      var match = self.lensFlow.layout === LAYOUT_FUTURE ? false : self.packageMatches(item);
      setHidden(item, !match);
      if (match) visibleCount += 1;
    });

    if (listEl) {
      setHidden(listEl, visibleCount === 0 && !!filteredEmpty);
    }
    if (filteredEmpty) {
      setHidden(filteredEmpty, visibleCount !== 0);
    }
  };

  BasariLensDrawer.prototype.selectPackage = function (button) {
    if (!button || button.closest('[hidden]')) return;

    var value = button.getAttribute('data-package-value') || '';
    var price = parseInt(button.getAttribute('data-package-price') || '0', 10) || 0;
    var money = button.getAttribute('data-package-money') || '';

    Array.prototype.forEach.call(this.drawer.querySelectorAll('[data-basari-lens-package]'), function (btn) {
      var active = btn === button;
      btn.classList.toggle(ACTIVE_CLASS, active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.lensFlow.packageValue = value;
    this.lensFlow.packagePrice = price;
    this.lensFlow.packageMoney = money;
    this.lensFlow.selectedPackage = {
      value: value,
      price: price,
      money: money
    };
    this.updateTotalBar();

    this.emit('basari:lens:package-change', {
      visionType: this.lensFlow.visionType,
      subtype: this.lensFlow.subtype,
      packageValue: value,
      packagePrice: price,
      packageMoney: money,
      selectedPackage: this.lensFlow.selectedPackage
    });

    this.navigate(STEP_PRESCRIPTION_HUB);
  };

  /**
   * Persist the merchant-selected prescription hub action and route forward.
   * @param {string} choice - Machine key (e.g. add_prescription)
   * @param {string} [destination] - Named route (e.g. prescription-methods)
   */
  BasariLensDrawer.prototype.setPrescriptionChoice = function (choice, destination) {
    var nextChoice = String(choice || '').trim();
    var nextDestination = String(destination || '').trim();

    this.lensFlow.prescriptionChoice = nextChoice || null;
    this.syncPrescriptionCards(nextChoice);
    this.resetPrescriptionMethod();

    this.emit('basari:lens:prescription-choice', {
      prescriptionChoice: this.lensFlow.prescriptionChoice,
      destination: nextDestination || null,
      visionType: this.lensFlow.visionType,
      selectedPackage: this.lensFlow.selectedPackage
    });

    if (nextDestination) {
      this.navigate(nextDestination);
    }
  };

  BasariLensDrawer.prototype.selectPrescriptionCard = function (card) {
    if (!card || card.closest('[hidden]')) return;

    var choice = card.getAttribute('data-prescription-choice') || '';
    var destination = card.getAttribute('data-destination-step') || '';
    this.setPrescriptionChoice(choice, destination);
  };

  BasariLensDrawer.prototype.syncPrescriptionCards = function (choice) {
    var key = String(choice || '');
    Array.prototype.forEach.call(this.drawer.querySelectorAll('[data-basari-prescription-card]'), function (btn) {
      var active = key !== '' && (btn.getAttribute('data-prescription-choice') || '') === key;
      btn.classList.toggle(ACTIVE_CLASS, active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  /**
   * Persist the selected prescription method and route to its destination.
   * @param {string} method - Machine key (e.g. manual)
   * @param {string} [destination] - Named route (e.g. manual-prescription)
   */
  BasariLensDrawer.prototype.setPrescriptionMethod = function (method, destination) {
    var nextMethod = String(method || '').trim();
    var nextDestination = String(destination || '').trim();

    this.lensFlow.prescriptionMethod = nextMethod || null;
    this.syncMethodCards(nextMethod);
    /* Eye-test booking is not a method child — clear it when method selection changes. */
    this.resetEyeTestBookingState();

    this.emit('basari:lens:prescription-method-selected', {
      method: this.lensFlow.prescriptionMethod,
      destination: nextDestination || null,
      lensFlow: {
        visionType: this.lensFlow.visionType,
        progressiveSubtype: this.lensFlow.progressiveSubtype,
        selectedPackage: this.lensFlow.selectedPackage,
        prescriptionChoice: this.lensFlow.prescriptionChoice,
        prescriptionMethod: this.lensFlow.prescriptionMethod,
        currentStep: this.lensFlow.currentStep
      }
    });

    if (nextDestination) {
      this.navigate(nextDestination);
    }
  };

  BasariLensDrawer.prototype.selectMethodCard = function (card) {
    if (!card || card.closest('[hidden]')) return;

    var method = card.getAttribute('data-prescription-method') || '';
    var destination = card.getAttribute('data-destination-step') || '';
    this.setPrescriptionMethod(method, destination);
  };

  BasariLensDrawer.prototype.syncMethodCards = function (method) {
    var key = String(method || '');
    Array.prototype.forEach.call(this.drawer.querySelectorAll('[data-basari-method-card]'), function (btn) {
      var active = key !== '' && (btn.getAttribute('data-prescription-method') || '') === key;
      btn.classList.toggle(ACTIVE_CLASS, active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  BasariLensDrawer.prototype.updateTotalBar = function () {
    if (!this.totalBar) return;

    var framePrice = parseInt(this.totalBar.getAttribute('data-frame-price') || '0', 10) || 0;
    var frameMoney =
      this.totalBar.getAttribute('data-frame-money') ||
      formatMoney(framePrice, this.totalBar.getAttribute('data-currency'));
    var lensPrice = this.lensFlow.packagePrice || 0;
    var currency = this.totalBar.getAttribute('data-currency') || 'INR';
    var total = framePrice + lensPrice;

    var frameEl = this.totalBar.querySelector('[data-basari-lens-total-frame]');
    var lensRow = this.totalBar.querySelector('[data-basari-lens-total-lens-row]');
    var lensEl = this.totalBar.querySelector('[data-basari-lens-total-lens]');
    var totalEl = this.totalBar.querySelector('[data-basari-lens-total-value]');

    if (frameEl) frameEl.textContent = frameMoney;

    if (lensPrice > 0 || this.lensFlow.packageMoney) {
      setHidden(lensRow, false);
      if (lensEl) {
        lensEl.textContent = this.lensFlow.packageMoney || formatMoney(lensPrice, currency);
      }
    } else {
      setHidden(lensRow, true);
      if (lensEl) lensEl.textContent = '';
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(total, currency);
    }

    this.totalBar.setAttribute('data-lens-price', String(lensPrice));
    this.totalBar.setAttribute('data-total-price', String(total));
  };

  BasariLensDrawer.prototype.emit = function (name, detail) {
    var payload = detail || {};
    payload.sectionId = this.drawer.getAttribute('data-section-id') || '';

    try {
      this.drawer.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: payload
        })
      );
    } catch (error) {
      // IE / older environments — ignore
    }
  };

  function initRoot(root) {
    if (!root || root.getAttribute(READY_ATTR) === 'true') return;
    if (!root.querySelector('[data-basari-lens-drawer]')) return;
    root.setAttribute(READY_ATTR, 'true');
    new BasariLensDrawer(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll
      ? (scope || document).querySelectorAll('[data-basari-product]')
      : [];
    var i;
    for (i = 0; i < roots.length; i += 1) {
      initRoot(roots[i]);
    }
  }

  function onSectionLoad(event) {
    var section = event.target;
    if (!section || !section.querySelector) return;
    if (!section.querySelector('[data-basari-product]')) return;

    section.querySelectorAll('[data-basari-product]').forEach(function (root) {
      root.removeAttribute(READY_ATTR);
    });
    initAll(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
})();
